import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Components
import Button from "./Button";

import config from "../../config";

// Image
import homecook_logo from "../../assets/img/homecook_logo.png";

// Icons
import { ReactComponent as HomeIcon } from "../../icons/HomeIcon.svg";
import { ReactComponent as HomeDarkIcon } from "../../icons/HomeIconFilled.svg";
import { ReactComponent as FoodEntrepreneurIcon } from "../../icons/FoodEntrepreneur.svg";
import { ReactComponent as FoodEntrepreneurDarkIcon } from "../../icons/FoodEntrepreneurDark.svg";
import { ReactComponent as ResourceProviders } from "../../icons/KitchenOperator.svg";
import { ReactComponent as ResourceProvidersDarkIcon } from "../../icons/KitchenOperatorDark.svg";
import { ReactComponent as NavDoucmentationIcon } from "../../icons/NavDocumentationIcon.svg";
import { ReactComponent as NavTutorialsIcon } from "../../icons/NavTutorialsIcon.svg";
import { ReactComponent as ArrowDownIcon } from "../../icons/FaqDownArrow.svg";
import { ReactComponent as ArrowUpIcon } from "../../icons/FaqUpArrow.svg";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentLocation = location.pathname;

  const [foodHover, setFoodHover] = useState(false);
  const [resourceHover, setResourceHover] = useState(false);

  const webUrl = config?.webUrl;
  const demoUrl = "/compliance/checklist";

  const goToHome = () => {
    navigate("/");
  };

  const goToFoodEntrepreneurs = () => {
    navigate("/food-entrepreneurs");
  };

  const goToResourceProviders = () => {
    navigate("/resource-providers");
  };

  /** Navigate to a section anchor on a marketing page from the nav dropdown. */
  const goToSection = (path: string, hash: string) => {
    navigate(`${path}${hash}`);
  };

  const navLinkText = (active: boolean) =>
    `font-raleway text-[12px] leading-tight sm:text-[13px] xl:text-[14px] text-center ${
      active ? "text-primaryDark-0 font-bold" : "text-text-300"
    }`;

  const isFoodActive = currentLocation === "/food-entrepreneurs";
  const isResourceActive = currentLocation === "/resource-providers";

  return (
    <nav className="fixed left-0 right-0 top-0 z-[100] h-[90px] border-b border-[#E9E9E9] bg-text-1000 px-4 sm:px-6 md:px-10 lg:px-14 xl:px-20 2xl:px-[120px]">
      <div className="mx-auto flex h-full min-w-0 max-w-[1550px] items-center justify-between gap-2 sm:gap-3 xl:gap-6">
        {/* Homecook Logo and Navigation Links */}
        <div className="flex min-h-0 min-w-0 flex-1 items-center gap-x-2 gap-y-0 sm:gap-x-3 md:gap-x-4 xl:gap-[30px]">
          {/* Homecook Logo */}
          <div className="flex shrink-0 cursor-pointer items-center gap-2" onClick={goToHome}>
            <img
              src={homecook_logo}
              alt="Homecook Logo"
              className="h-7 w-7 shrink-0 sm:h-8 sm:w-8"
            />
            <h4 className="font-raleway text-lg font-bold text-primaryDark-0 sm:text-xl xl:text-[24px]">
              HomeCook
            </h4>
          </div>

          {/* Navigation Links - scroll horizontally when the row is tighter than content (keeps bar at 90px for page offsets) */}
          <div className="flex min-h-0 min-w-0 flex-1 items-center justify-start gap-x-2 overflow-x-auto overflow-y-visible py-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-x-3 md:gap-4 xl:gap-[32px] xl:overflow-x-visible [&::-webkit-scrollbar]:hidden [&_svg]:h-4 [&_svg]:w-4 xl:[&_svg]:h-5 xl:[&_svg]:w-5">
            {/* Home Link */}
            <div
              onClick={goToHome}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 hover:scale-95 sm:gap-2 xl:gap-[8px]"
            >
              {currentLocation === "/" ? (
                <HomeDarkIcon className="text-primaryDark-0" />
              ) : (
                <HomeIcon className="text-text-300" />
              )}
              <p className={navLinkText(currentLocation === "/")}>Home</p>
            </div>

            {/* Food Entrepreneurs Link */}
            <div
              onMouseEnter={() => setFoodHover(true)}
              onMouseLeave={() => setFoodHover(false)}
              className="relative shrink-0"
            >
              <div
                onClick={goToFoodEntrepreneurs}
                className="flex cursor-pointer items-center gap-1.5 sm:gap-2 xl:gap-[8px]"
              >
                {isFoodActive ? (
                  <FoodEntrepreneurDarkIcon className="text-primaryDark-0" />
                ) : (
                  <FoodEntrepreneurIcon className="text-text-300" />
                )}
                <p className={navLinkText(isFoodActive)}>Food Entrepreneurs</p>
                {foodHover ? (
                  <ArrowUpIcon
                    className={`shrink-0 ${isFoodActive ? "text-primaryDark-0" : "text-text-300"}`}
                  />
                ) : (
                  <ArrowDownIcon
                    className={`shrink-0 ${isFoodActive ? "text-primaryDark-0" : "text-text-300"}`}
                  />
                )}
              </div>

              {foodHover && (
                <div className="absolute left-0 top-full z-20 pt-1">
                  <div className="min-w-[220px] w-max rounded-lg bg-white py-[8px] shadow-lg">
                    <button
                      type="button"
                      onClick={() => goToSection("/food-entrepreneurs", "#food-how")}
                      className="flex h-[40px] w-full items-center gap-[8px] px-[25px] mb-[8px] text-left text-[14px] text-text-300 transition-all hover:font-semibold hover:text-primaryDark-400"
                    >
                      <NavDoucmentationIcon className="h-[20px] w-[20px]" />
                      How It Works
                    </button>
                    <button
                      type="button"
                      onClick={() => goToSection("/food-entrepreneurs", "#food-how-box")}
                      className="flex h-[40px] w-full items-center gap-[8px] px-[25px] text-left text-[14px] text-text-300 transition-all hover:font-semibold hover:text-primaryDark-400"
                    >
                      <NavTutorialsIcon className="h-[20px] w-[20px]" />
                      View Tutorials
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Resource Providers Link */}
            <div
              onMouseEnter={() => setResourceHover(true)}
              onMouseLeave={() => setResourceHover(false)}
              className="relative shrink-0"
            >
              <div
                onClick={goToResourceProviders}
                className="flex cursor-pointer items-center gap-1.5 sm:gap-2 xl:gap-[8px]"
              >
                {isResourceActive ? (
                  <ResourceProvidersDarkIcon className="text-primaryDark-0" />
                ) : (
                  <ResourceProviders className="text-text-300" />
                )}
                <p className={navLinkText(isResourceActive)}>Resource Providers</p>
                {resourceHover ? (
                  <ArrowUpIcon
                    className={`shrink-0 ${isResourceActive ? "text-primaryDark-0" : "text-text-300"}`}
                  />
                ) : (
                  <ArrowDownIcon
                    className={`shrink-0 ${isResourceActive ? "text-primaryDark-0" : "text-text-300"}`}
                  />
                )}
              </div>

              {resourceHover && (
                <div className="absolute left-0 top-full z-20 pt-1">
                  <div className="min-w-[220px] w-max rounded-lg bg-white py-[8px] shadow-lg">
                    <button
                      type="button"
                      onClick={() => goToSection("/resource-providers", "#resource-how")}
                      className="flex h-[40px] w-full items-center gap-[8px] px-[20px] mb-[8px] text-left text-[14px] text-text-300 transition-all hover:font-semibold hover:text-primaryDark-400"
                    >
                      <NavDoucmentationIcon className="h-[20px] w-[20px]" />
                      How It Works
                    </button>
                    <button
                      type="button"
                      onClick={() => goToSection("/resource-providers", "#resource-how-box")}
                      className="flex h-[40px] w-full items-center gap-[8px] px-[20px] text-left text-[14px] text-text-300 transition-all hover:font-semibold hover:text-primaryDark-400"
                    >
                      <NavTutorialsIcon className="h-[20px] w-[20px]" />
                      View Tutorials
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Buttons: compact on narrow desktop; reset Button <p> margins for vertical alignment */}
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3 xl:gap-4 [&_button]:max-h-[52px] [&_button]:whitespace-normal [&_button]:px-2.5 [&_button]:py-2 [&_button]:text-[11px] [&_button]:leading-tight min-[1100px]:[&_button]:px-3 min-[1100px]:[&_button]:text-xs min-[1200px]:[&_button]:px-[24px] min-[1200px]:[&_button]:py-[12px] min-[1200px]:[&_button]:text-[14px] min-[1200px]:[&_button]:leading-normal [&_p]:m-0">
          <Button
            label={"View Resource Pantry"}
            variant="primary"
            onClick={() => {
              window.open(webUrl, "_blank");
            }}
          />
          <Button
            label={"Check Readiness"}
            variant="secondary"
            onClick={() => {
              window.open(demoUrl, "_blank");
            }}
          />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
